/**
 * MUTAGIM.SHOP — Checkout Upsell Popup (Phase 15)
 * Pop-up suggestion si panier < 100€ pour livraison gratuite
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { X, Truck, Plus, Gift, ArrowRight, ShoppingBag } from 'lucide-react';
import { useCurrency } from '../contexts/CurrencyContext';
import { useCart } from '../contexts/CartContext';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function CheckoutUpsellPopup({ cartTotal, onClose, className }) {
  const { t, i18n } = useTranslation();
  const { formatPrice } = useCurrency();
  const { addToCart } = useCart();
  const [upsellData, setUpsellData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(null);
  const lang = i18n.language;

  useEffect(() => {
    const fetchUpsell = async () => {
      try {
        const res = await fetch(`${API_URL}/api/promotions/checkout-upsell?cart_total=${cartTotal}`);
        if (res.ok) {
          const data = await res.json();
          setUpsellData(data);
        }
      } catch (err) {
        console.error('Error fetching checkout upsell:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchUpsell();
  }, [cartTotal]);

  const handleAdd = async (product, e) => {
    e.preventDefault();
    e.stopPropagation();
    setAdding(product.id);
    try {
      await addToCart(product.id);
      toast.success(t('cart.added', 'Ajouté au panier'));
      // Close popup after adding
      setTimeout(() => onClose?.(), 1000);
    } catch (err) {
      toast.error('Erreur');
    } finally {
      setAdding(null);
    }
  };

  // Ne pas afficher si pas de données ou pas besoin d'upsell
  if (loading || !upsellData?.show_upsell) return null;

  const { products, message, remaining, threshold } = upsellData;
  const progress = ((cartTotal / threshold) * 100).toFixed(0);

  return (
    <div className={cn(
      "fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm",
      className
    )}>
      <div 
        className="relative w-full max-w-lg bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl border border-gray-700 shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300"
        data-testid="checkout-upsell-popup"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-gray-800/80 hover:bg-gray-700 text-gray-400 hover:text-white transition-all"
          data-testid="close-upsell-popup"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header with truck icon */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-500 p-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20 mb-3">
            <Truck className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-xl font-bold text-white">
            {message?.[lang] || message?.fr || `Plus que ${remaining.toFixed(0)}€ pour la livraison gratuite !`}
          </h2>
        </div>

        {/* Progress bar */}
        <div className="px-6 -mt-3">
          <div className="bg-gray-800 rounded-full p-1">
            <div className="relative">
              <Progress value={Math.min(parseFloat(progress), 100)} className="h-3 bg-gray-700" />
              <div className="absolute right-0 top-1/2 -translate-y-1/2 transform translate-x-2">
                <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                  <Gift className="w-3 h-3 text-white" />
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-between text-sm mt-2 px-1">
            <span className="text-gray-400">{formatPrice({ EUR: cartTotal })}</span>
            <span className="text-green-400 font-semibold">{formatPrice({ EUR: threshold })} = Livraison gratuite</span>
          </div>
        </div>

        {/* Products suggestions */}
        <div className="p-6">
          <p className="text-gray-400 text-sm mb-4 text-center">
            {t('promotions.addOneMore', 'Ajoutez un de ces produits pour débloquer la livraison gratuite :')}
          </p>
          
          <div className="grid grid-cols-3 gap-3">
            {products?.slice(0, 3).map((product) => (
              <div
                key={product.id}
                className="group bg-gray-800/50 rounded-lg overflow-hidden border border-gray-700 hover:border-green-500/50 transition-all"
              >
                <Link to={`/product/${product.slug}`} className="block">
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={product.image || '/placeholder-product.jpg'}
                      alt={product.name?.[lang] || ''}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  </div>
                </Link>
                <div className="p-2">
                  <p className="text-white text-xs font-medium line-clamp-1 mb-1">
                    {product.name?.[lang] || product.name?.fr}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-green-400 font-bold text-sm">
                      {formatPrice({ EUR: product.price })}
                    </span>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={(e) => handleAdd(product, e)}
                      disabled={adding === product.id}
                      className="w-6 h-6 text-gray-400 hover:text-white hover:bg-green-500/20"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-6">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800"
            >
              {t('common.noThanks', 'Non merci')}
            </Button>
            <Button
              onClick={onClose}
              className="flex-1 bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-700 hover:to-emerald-600 text-white"
            >
              {t('checkout.continue', 'Continuer')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CheckoutUpsellPopup;
